import React, { useState, useEffect } from 'react';
import {
  stagedEmployeeDocumentsColumns,
  getStagedEmployeeDocumentsListData,
} from './StagedDocumentsDataGrid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useSelector } from 'react-redux';
import { intialPageSize } from 'constants';
import { EndGrid } from 'components/Ui/styled';
import { HeadingMuiTypography } from 'components/Ui/styled';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import {
  pageSizeChangeHandler,
  useNotification,
  showBackdropLoader,
  getSnackBar,
} from 'utils/commonUtils';
import {
  getStagedEmployeeDocuments,
  postDownloadStagedDocument,
  deleteStagedDocument,
} from 'layouts/documentManagement/services/documentManagementApi';
import ConfirmationDialog from 'components/Common/ConfirmationDialog/ConfirmationDialog';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';
import { useDispatch } from 'react-redux';
import StagedDocumentUpload from './StagedDocumentUpload';
import { setStagedEmployeeDocuments } from 'layouts/documentManagement/store/stagedEmployeeDocumentsSlice/stagedEmployeeDocumentsSlice';

const EmployeeDocumentsTab = () => {
  const dispatch = useDispatch();
  const { handleErrorResponse, NotificationPopup } = useNotification();

  const [stagedEmployeeDocuments, setStagedEmployeeDocumentsData] = useState({
    isLoading: false,
    rows: [],
    pageSize: intialPageSize,
    page: 1,
  });
  const [loadingButton, setLoadingButton] = useState(false);
  const [isLoadingBackDrop, setIsLoadingBackDrop] = useState(false);
  const [deleteDocument, setDeleteDocument] = useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [stagedDocumentUploadDialog, setStagedDocumentUploadDialog] =
    useState(false);

  const { pageSize, page } = stagedEmployeeDocuments;

  const employee = useSelector((state) => state.employee?.employee);

  const pageSizeChange = pageSizeChangeHandler(setStagedEmployeeDocumentsData);

  useEffect(() => {
    fetchStagedEmployeeDocuments();
  }, []);

  const fetchStagedEmployeeDocuments = async () => {
    setStagedEmployeeDocumentsData({ ...stagedEmployeeDocuments, isLoading: true });
    try {
      const stagedEmployeeDocumentsData = await getStagedEmployeeDocuments(
        employee
      );
      dispatch(
        setStagedEmployeeDocuments(stagedEmployeeDocumentsData?.documents)
      );
      setStagedEmployeeDocumentsData((prev) => ({
        ...prev,
        isLoading: false,
        rows: getStagedEmployeeDocumentsListData(stagedEmployeeDocumentsData),
      }));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleDownloadStagedDocument = async (params) => {
    const { row } = params;
    const { filename } = row;
    try {
      setIsLoadingBackDrop(true);
      const downloadLink = await postDownloadStagedDocument(employee, filename);
      const response = await fetch(downloadLink?.link);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      const snackBar = getSnackBar('File downloaded successfully');
      dispatch(displaySnackbar(snackBar));
      setIsLoadingBackDrop(false);
    } catch (error) {
      setIsLoadingBackDrop(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleDeleteStagedDocument = (params) => {
    const { row } = params;
    const { filename } = row;
    setDeleteDocument(filename);
    setOpenConfirmationDialog(true);
  };

  const handleDeleteStagedDocumentApi = async () => {
    try {
      setLoadingButton(true);
      await deleteStagedDocument(employee, deleteDocument);
      fetchStagedEmployeeDocuments();
      setLoadingButton(false);
      const snackBar = getSnackBar('Succesfully document has been deleted');
      dispatch(displaySnackbar(snackBar));
      setOpenConfirmationDialog(false);
    } catch (error) {
      setLoadingButton(false);
      setOpenConfirmationDialog(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleStagedDocumentUpload = () => {
    setStagedDocumentUploadDialog(true);
  };

  return (
    <>
      {showBackdropLoader(isLoadingBackDrop)}
      <HeadingMuiTypography variant="h6">Staged Documents</HeadingMuiTypography>
      <EndGrid>
        <Button variant="text" onClick={handleStagedDocumentUpload}>
          Upload Document
        </Button>
      </EndGrid>
      <Box mt={1}></Box>
      <DeltaDataGrid
        listData={stagedEmployeeDocuments}
        columns={stagedEmployeeDocumentsColumns(
          handleDownloadStagedDocument,
          handleDeleteStagedDocument
        )}
        setListData={pageSizeChange}
      />
      <StagedDocumentUpload
        open={stagedDocumentUploadDialog}
        setOpen={setStagedDocumentUploadDialog}
        fetchStagedEmployeeDocuments={fetchStagedEmployeeDocuments}
      />
      <ConfirmationDialog
        openConfirmationDialog={openConfirmationDialog}
        setOpenConfirmationDialog={setOpenConfirmationDialog}
        title="Are you sure you want to delete this document ?"
        handleClick={handleDeleteStagedDocumentApi}
        loadingButton={loadingButton}
      />
      <NotificationPopup />
    </>
  );
};

export default EmployeeDocumentsTab;
